(() => {
  const updateCartBubble = (count) => {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    let bubble = cartIcon.querySelector('.cart-count-bubble');
    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        cartIcon.appendChild(bubble);
      }
      const visible = bubble.querySelector('[aria-hidden="true"]');
      if (visible) visible.textContent = count < 100 ? String(count) : '';
      const hidden = bubble.querySelector('.visually-hidden');
      if (hidden) hidden.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
    } else if (bubble) {
      bubble.remove();
    }
  };

  const syncAddedButtons = (root, cart) => {
    const variantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));
    root.querySelectorAll('[data-zs-silent-add]').forEach((button) => {
      const added = variantIds.has(String(button.dataset.variantId));
      button.classList.toggle('is-added', added);
      button.disabled = added;
      const span = button.querySelector('span');
      if (span) span.textContent = added ? button.dataset.addedLabel : button.dataset.addLabel;
    });
  };

  const getCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Could not refresh cart');
    return response.json();
  };

  // Refresh the existing drawer markup without calling CartDrawer.renderContents(),
  // which would open the drawer and change the intended silent-add behavior.
  const refreshSilentAddDrawer = async () => {
    if (!window.matchMedia('(max-width: 989px)').matches) return;
    const drawer = document.querySelector('cart-drawer');
    const currentInner = drawer?.querySelector('#CartDrawer .drawer__inner');
    if (!currentInner) return;

    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer');
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not refresh cart drawer');
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error('Cart drawer section unavailable');
    const updated = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const freshDrawer = updated.querySelector('cart-drawer');
    const freshInner = freshDrawer?.querySelector('#CartDrawer .drawer__inner');
    if (!freshInner) throw new Error('Cart drawer markup unavailable');

    // Preserve the cart-drawer custom element, overlay listener, and open/closed state.
    currentInner.innerHTML = freshInner.innerHTML;
    drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
    currentInner.classList.toggle('is-empty', freshInner.classList.contains('is-empty'));
  };

  const silentAdd = async (root, button) => {
    if (button.disabled || button.dataset.loading === 'true') return;
    button.dataset.loading = 'true';
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Could not add item');

      const cart = await getCart();
      updateCartBubble(cart.item_count || 0);
      syncAddedButtons(root, cart);
      // Cart add already succeeded. A section-rendering failure must not report an add failure.
      try { await refreshSilentAddDrawer(); } catch (error) { console.warn('Cart drawer refresh failed', error); }
      document.dispatchEvent(new CustomEvent('zs:cart-updated', { detail: { cart } }));
    } catch (error) {
      const span = button.querySelector('span');
      if (span) span.textContent = 'Try again';
      window.setTimeout(() => {
        if (span && !button.disabled) span.textContent = button.dataset.addLabel;
      }, 1800);
    } finally {
      button.dataset.loading = 'false';
      button.removeAttribute('aria-busy');
    }
  };

  const init = (root) => {
    if (!root || root.dataset.zsReady === 'true') return;
    root.dataset.zsReady = 'true';

    const facts = root.querySelector('[data-zs-facts]');
    const factsDesktop = root.querySelector('[data-zs-facts-desktop]');
    let factsHome = null;
    if (facts) {
      factsHome = document.createComment('zs-quick-facts-home');
      facts.parentNode.insertBefore(factsHome, facts);
      const placeFacts = () => {
        if (window.matchMedia('(min-width: 990px)').matches) {
          facts.open = true;
          if (factsDesktop && facts.parentNode !== factsDesktop) factsDesktop.appendChild(facts);
        } else {
          facts.open = false;
          if (factsHome && factsHome.parentNode && facts.parentNode !== factsHome.parentNode) factsHome.parentNode.insertBefore(facts, factsHome.nextSibling);
        }
      };
      placeFacts();
      const mq = window.matchMedia('(min-width: 990px)');
      if (mq.addEventListener) mq.addEventListener('change', placeFacts);
      else if (mq.addListener) mq.addListener(placeFacts);
    }

    const hero = root.querySelector('[data-zs-hero]');
    root.querySelectorAll('[data-zs-image]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!hero) return;
        hero.src = btn.dataset.zsImage;
        hero.alt = btn.dataset.zsAlt || '';
        root.querySelectorAll('[data-zs-image]').forEach((b) => b.classList.toggle('is-active', b === btn));
      });
    });

    const updateAvailabilityState = () => {
      const selector = root.querySelector('[data-zs-variant]');
      const selected = selector?.selectedOptions?.[0];
      const available = selected ? selected.dataset.available === 'true' : root.dataset.zsDefaultAvailable === 'true';
      const add = root.querySelector('.zs-add');
      const stickyButton = root.querySelector('[data-zs-sticky-add]');
      if (add) { add.disabled = !available; add.setAttribute('aria-disabled', String(!available)); }
      if (stickyButton) {
        stickyButton.textContent = available ? 'Add to cart' : 'Sold out';
        stickyButton.disabled = !available;
        stickyButton.setAttribute('aria-disabled', String(!available));
      }
    };
    updateAvailabilityState();
    const variant = root.querySelector('[data-zs-variant]');
    if (variant) {
      variant.addEventListener('change', () => {
        const form = root.querySelector('product-form form');
        const idInput = form && form.querySelector('input[name="id"]');
        if (idInput) idInput.value = variant.value;
        const price = root.querySelector('[data-zs-price]');
        if (price) price.textContent = variant.selectedOptions[0].dataset.price || price.textContent;
        updateAvailabilityState();
      });
    }

    root.querySelectorAll('[data-zs-back]').forEach((back) => {
      back.addEventListener('click', (e) => {
        if (history.length > 1 && document.referrer && document.referrer.includes(location.host)) {
          e.preventDefault();
          history.back();
        }
      });
    });

    root.querySelectorAll('[data-zs-silent-add]').forEach((button) => {
      button.addEventListener('click', () => silentAdd(root, button));
    });

    // Trade publishes this after a drawer removal or quantity change.
    // Its cartData already contains the authoritative updated item list.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData?.items) syncAddedButtons(root, event.cartData);
        else getCart().then((cart) => syncAddedButtons(root, cart)).catch(() => {});
      });
    }

    getCart().then((cart) => {
      updateCartBubble(cart.item_count || 0);
      syncAddedButtons(root, cart);
    }).catch(() => {});

    const sticky = root.querySelector('[data-zs-sticky-add]');
    if (sticky) sticky.addEventListener('click', () => {
      const form = root.querySelector('product-form form');
      if (form && !sticky.disabled) form.requestSubmit();
    });
  };

  document.querySelectorAll('[data-zs-product-section]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector && e.target.querySelector('[data-zs-product-section]');
    if (root) init(root);
  });
})();
